import styled, { useTheme } from "styled-components";
import { ChevronRight } from "lucide-react";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import {
  newLookSortersStyle,
  OrdersCount,
} from "features/trades/NewLook/drawerDetails.ts";
import useMedia from "utils/hooks/useMedia";
import { TRADE_ORDERS_SECTION_ID } from "testIds";
import { MOBILE_SEARCH } from "common/constants";
import Title from "common/Typography/Title";
import OrderCard from "features/trades/TradeDrawer/Orders/OrderCard";
import AddOrderButton from "features/trades/common/AddOrderButton";
import AddOrEditOrderModal from "features/trades/EditTrade/AddOrEditOrderModal";
import React, { useCallback, useMemo, useState } from "react";
import OrderSectionErrorsSummary from "features/trades/common/OrderSectionErrorsSummary";
import CardSorters from "common/CardSorters/CardSorters";
import { toggleArrayItem } from "utils/arrays";
import Flex from "common/Boxes/Flex";
import ExpandCollapse from "common/Animations/ExpandCollapse";
import RightOutlined from "@ant-design/icons/RightOutlined";
import { OrderTypes } from "@shared/features/trades/common/types";
import { EditOrderData } from "@shared/features/trades/EditTrade/types";
import { ExpandCollapseDirection } from "@shared/common/CardSorters/types";
import { useOrdersSection } from "@shared/features/trades/EditTrade/hooks";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { TranslationKeys } from "@shared/common/i18n/types.ts";

interface OrdersSectionProps {
  orders?: EditOrderData[];
  title: string;
  hideAmounts?: boolean;
  isEdit?: boolean;
  orderType: OrderTypes;
}

const OrdersSection = ({
  orders,
  title,
  hideAmounts,
  isEdit,
  orderType,
}: OrdersSectionProps) => {
  const { t, i18n } = useTypedTranslation(LanguageNameSpaces.TRADES);
  const theme = useTheme();
  const { isNewLook } = theme;
  const [sectionExpanded, setSectionExpanded] = useState(true);
  const [expandedIndices, setExpandedIndices] = useState<number[]>([]);
  const [currentExpandCollapseDirection, setCurrentExpandCollapse] = useState(
    ExpandCollapseDirection.EXPAND
  );

  const {
    addOrderModalOpen,
    setAddOrderModalOpen,
    getAddOrderDisabledMessage,
    onAddOrderClick,
    ordersErrors,
    errorsExtraData,
  } = useOrdersSection();

  const expandAll = () => {
    setExpandedIndices([...Array(orders?.length).keys()]);
    setCurrentExpandCollapse(ExpandCollapseDirection.COLLAPSE);
  };

  const collapseAll = () => {
    setExpandedIndices([]);
    setCurrentExpandCollapse(ExpandCollapseDirection.EXPAND);
  };

  const onExpandCollapseAllClick = () => {
    if (expandedIndices.length === 0) expandAll();
    else if (expandedIndices.length === orders?.length) collapseAll();
    else if (
      currentExpandCollapseDirection === ExpandCollapseDirection.COLLAPSE
    )
      collapseAll();
    else if (currentExpandCollapseDirection === ExpandCollapseDirection.EXPAND)
      expandAll();
  };

  const toggleExpanded = useCallback((index: number) => {
    setExpandedIndices((expandedIndices) =>
      toggleArrayItem(expandedIndices, index)
    );
  }, []);

  const columnNames = useMemo(
    () =>
      hideAmounts
        ? [t("orders.ratio"), t("orders.price")]
        : [t("orders.ratio"), t("orders.price"), t("orders.amount")],
    [hideAmounts, i18n.language]
  );
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  // The new look puts the order status on the same row (desktop), so the
  // value columns leave room for it on the right.
  const useStatusColumn = isNewLook && !isMobile;
  const columnSpans = useMemo(() => {
    if (useStatusColumn) return hideAmounts ? [9, 8] : [6, 6, 6];
    return hideAmounts ? [16, 8] : [8, 8, 8];
  }, [hideAmounts, useStatusColumn]);
  const ordersSectionErrors = ordersErrors?.[orderType];
  const cardSorters = (
    <CardSorters
      columnNames={columnNames}
      columnSpans={columnSpans}
      style={
        isNewLook
          ? newLookSortersStyle(theme)
          : { padding: "6px 16px", margin: "16px 0 12px 0" }
      }
      showExpandCollapse={true}
      onExpandCollapseClick={onExpandCollapseAllClick}
    />
  );
  const errorsSummary = isEdit &&
    ordersSectionErrors &&
    Object.keys(ordersSectionErrors.section || {}).length > 0 && (
      <OrderSectionErrorsSummary
        errors={ordersSectionErrors?.section}
        orderType={orderType}
        style={{ marginTop: 12 }}
        errorsExtraData={errorsExtraData}
      />
    );
  const addOrderModal = addOrderModalOpen && (
    <AddOrEditOrderModal
      closeModal={() => setAddOrderModalOpen(false)}
      title={t(ORDER_TYPE_TO_TITLE_DISPLAY[orderType])}
      orderType={orderType}
      onOk={(order: EditOrderData) => onAddOrderClick(orderType, order)}
    />
  );

  const addOrderButton = isEdit && (
    <AddOrderButton
      addOrder={() => setAddOrderModalOpen(true)}
      disabledMessage={getAddOrderDisabledMessage(orderType, orders)}
    />
  );

  return (
    <Wrapper data-testid={TRADE_ORDERS_SECTION_ID}>
      {isNewLook ? (
        <NewLookHead>
          <SectionToggle
            type="button"
            onClick={() => setSectionExpanded(!sectionExpanded)}
            aria-expanded={sectionExpanded}
          >
            {title}
            {!!orders?.length && <OrdersCount>{orders.length}</OrdersCount>}
            <Chevron size={16} strokeWidth={2} $expanded={sectionExpanded} />
          </SectionToggle>
          {addOrderButton}
        </NewLookHead>
      ) : (
        <Title
          as={"div"}
          level={4}
          style={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Flex
            onClick={() => setSectionExpanded(!sectionExpanded)}
            style={{ cursor: "pointer" }}
          >
            <div>{title}</div>
            <Arrow $expanded={sectionExpanded} />
          </Flex>
          {addOrderButton}
        </Title>
      )}
      <ExpandCollapse expanded={sectionExpanded}>
        {errorsSummary}
        {(!isNewLook || !!orders?.length) && cardSorters}
        <Flex $flexDirection="column" style={{ gap: isNewLook ? 8 : 16 }}>
          {orders?.map((order, index) => (
            <OrderCard
              key={index}
              order={order}
              hideAmounts={!!hideAmounts}
              index={index}
              isEdit={isEdit}
              orderType={orderType}
              columnSpans={columnSpans}
              expanded={expandedIndices.includes(index)}
              setExpanded={toggleExpanded}
            />
          ))}
        </Flex>
      </ExpandCollapse>
      {addOrderModal}
    </Wrapper>
  );
};

export default OrdersSection;

export const ORDER_TYPE_TO_TITLE_DISPLAY: Record<
  OrderTypes,
  TranslationKeys<LanguageNameSpaces.TRADES>
> = {
  [OrderTypes.ENTRY]: "orders.add_entry_order",
  [OrderTypes.TAKE_PROFIT]: "orders.add_take_profit_order",
  [OrderTypes.STOP]: "orders.add_stop_order",
};

const Wrapper = styled.div`
  width: 100%;
`;
const NewLookHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 32px;
`;
const SectionToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
  margin-left: -6px;
  padding: 0 6px;
  border: none;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  background: transparent;
  font-family: inherit;
  cursor: pointer;
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  &:hover {
    background: ${(p) => getNewLookTokens(p.theme).rowHover};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 1px;
  }
`;
const Chevron = styled(ChevronRight)<{ $expanded: boolean }>`
  color: ${(p) => getNewLookTokens(p.theme).muted};
  transform: rotate(${(p) => (p.$expanded ? 90 : 0)}deg);
  transition: transform 0.25s;
`;
const Arrow = styled(RightOutlined)<{ $expanded: boolean }>`
  transform: rotate(${(p) => (p.$expanded ? 90 : 0)}deg);
  margin-left: 8px;
  transition: transform 0.35s;
  display: block;
`;
